import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";

export interface FleetMembershipSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  is_platform_admin: boolean;
  fleets: FleetMembershipSummary[];
}

interface AuthContextData {
  user: UserProfile | null;
  loading: boolean;
  accessDenied: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextData>({
  user: null,
  loading: true,
  accessDenied: false,
  refresh: async () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/auth/me"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        setUser(res.body as UserProfile);
        setAccessDenied(false);
      } else if (res.code === 403) {
        setUser(null);
        setAccessDenied(true);
      } else {
        setUser(null);
        setAccessDenied(false);
      }
    } catch {
      setUser(null);
      setAccessDenied(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiClient["/api/v1/admin/auth/logout"]["post"]({
        parameters: {},
        body: null,
      });
    } catch {
      // best-effort
    }
    setUser(null);
    setAccessDenied(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, accessDenied, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
